import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import LoadingScreen from '../components/LoadingScreen';

interface LoadingContextType {
  setLoading: (loading: boolean) => void;
  isLoading: boolean;
}

const LoadingContext = createContext<LoadingContextType | undefined>(undefined);

export const LoadingProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [showLoadingScreen, setShowLoadingScreen] = useState(true);

  // Handle loading state changes
  const handleSetLoading = useCallback((loading: boolean) => {
    if (loading) {
      setShowLoadingScreen(true);
      setIsLoading(true);
    } else {
      // When turning off loading, first update internal state
      setIsLoading(false);
      // Then wait for exit animation to complete before hiding the screen
      setTimeout(() => {
        setShowLoadingScreen(false);
      }, 500); // Match this with the exit animation duration
    }
  }, []);

  // Initial loading simulation
  React.useEffect(() => {
    const timer = setTimeout(() => {
      handleSetLoading(false);
    }, 2000);
    return () => clearTimeout(timer);
  }, [handleSetLoading]);

  return (
    <LoadingContext.Provider value={{ setLoading: handleSetLoading, isLoading }}>
      <LoadingScreen isVisible={showLoadingScreen} />
      <div 
        className="transition-opacity duration-300"
        style={{ 
          opacity: isLoading ? 0 : 1,
          visibility: showLoadingScreen ? 'hidden' : 'visible' 
        }}
      >
        {children}
      </div>
    </LoadingContext.Provider>
  );
};

export const useLoading = () => {
  const context = useContext(LoadingContext);
  if (context === undefined) {
    throw new Error('useLoading must be used within a LoadingProvider');
  }
  return context;
};
